/**
 * Categorized between-story + in-editor store — expressive sidegrades only.
 * Items change controls / cosmetics / workflow / option inventories. Never Reach multipliers.
 *
 * Ownership gates which authored option IDs a player may newly apply.
 * Scoring / challenge / render never read ownership for an existing valid graph.
 */
import type { StoreItemId } from "./types.js";
import type { ToolId } from "../shell/ui/tools.js";

export type { StoreItemId };
export type { ToolId };

/** Player-facing currency label. Underlying economy field stays `cash`. */
export const CURRENCY_LABEL = "Bits";

export type StoreCategoryId =
  | "headline-style"
  | "sfx"
  | "looks"
  | "motion"
  | "decorate"
  | "utility"
  | "cosmetic";

export type CreativeOptionKind =
  | "headline"
  | "sfx"
  | "look"
  | "motion"
  | "sticker"
  | "transition";

export type ResultCardThemeId = "default" | "portfolio-alt";

export interface StoreItemGrants {
  /** Headline template IDs unlocked across packs. */
  headlineTemplateIds?: readonly string[];
  sfxOptionIds?: readonly string[];
  lookIds?: readonly string[];
  motionIds?: readonly string[];
  stickerIds?: readonly string[];
  transitionIds?: readonly string[];
  /** Utility / Fine-tune features (not creative card IDs). */
  features?: readonly StoreFeatureId[];
  /** Cosmetic themes made available to equip. */
  themes?: readonly ResultCardThemeId[];
}

export type StoreFeatureId =
  | "precision-trim"
  | "caption-deep"
  | "sting-pack"
  | "draft-slot"
  | "seamless-transitions";

export interface StoreCatalogItem {
  id: StoreItemId;
  category: StoreCategoryId;
  /** Edit-tool this upgrade affects — drives the store's category tabs. */
  tool: ToolId;
  /** Sub-category within the tool (e.g. Look → Filters). */
  sub: string;
  title: string;
  blurb: string;
  /** Cost in Bits. */
  cost: number;
  /** Plain-language gameplay effect when owned. */
  effect: string;
  /** Explicit authored IDs / features this item unlocks. */
  grants: StoreItemGrants;
}

/** Tool categories, in editor-dock order — the store's tab set. */
export const STORE_TOOL_ORDER: readonly ToolId[] = [
  "cut",
  "frame",
  "text",
  "sound",
  "look",
  "motion",
  "cast",
  "decorate",
] as const;

export const STORE_CATEGORY_ORDER: readonly StoreCategoryId[] = [
  "headline-style",
  "sfx",
  "looks",
  "motion",
  "decorate",
  "utility",
  "cosmetic",
] as const;

export const STORE_CATEGORY_LABELS: Record<StoreCategoryId, string> = {
  "headline-style": "Headlines",
  sfx: "SFX",
  looks: "Looks",
  motion: "Motion",
  decorate: "Stickers",
  utility: "Tools",
  cosmetic: "Themes",
};

/**
 * Free creative option IDs — always selectable without purchase.
 * Minimum useful free set per product requirements.
 */
export const FREE_CREATIVE_OPTIONS: Readonly<
  Record<CreativeOptionKind, ReadonlySet<string>>
> = {
  headline: new Set([
    "hl-vet-accusation",
    "hl-vet-abandoned",
    "hl-bowl-hungry",
    "hl-bowl-took",
    "hl-phone-stole",
    "hl-phone-snatched",
    "hl-phone-found",
  ]),
  sfx: new Set([
    "sfx-opt-whoosh",
    "sfx-opt-impact",
    "sfx-opt-notify-positive",
  ]),
  look: new Set([
    "look-clean",
    "look-warm-glow",
    "look-cool-cast",
    // Expanded grades — free cosmetic grades (no Reach change).
    "look-noir",
    "look-golden-hour",
    "look-vhs",
    "look-vibrant",
    "look-faded",
    "look-neon",
    // Advanced editor FX looks — free cosmetic atmospherics (no Reach change).
    "look-focus-blur",
    "look-rain",
    "look-lightning",
  ]),
  motion: new Set(["motion-steady", "motion-push-in"]),
  /**
   * Free starter set: 8 versatile stickers (a clean 4x2 grid on first open)
   * so new players always have a useful default kit. The other 18 stickers
   * live in themed cost packs below (see `sticker-pack-reactions` /
   * `-effects`) — every catalog sticker id must be free here OR granted by
   * exactly one pack.
   */
  sticker: new Set([
    "sticker-wow",
    "sticker-arrow",
    "sticker-fire",
    "sticker-eyes",
    "sticker-heart",
    "sticker-joy",
    "sticker-sunglasses",
    "sticker-crown",
  ]),
  transition: new Set(["transition-hard-cut", "transition-flash"]),
};

export const STORE_CATALOG: readonly StoreCatalogItem[] = [
  {
    id: "caption-style-reveal",
    category: "headline-style",
    tool: "text",
    sub: "Styles",
    title: "Caption style pack",
    blurb: "Extra kinetic styles and word-reveal controls.",
    cost: 14,
    effect: "Unlocks extra caption styles and word-reveal timing.",
    grants: { features: ["caption-deep"] },
  },
  {
    id: "headline-pack",
    category: "headline-style",
    tool: "text",
    sub: "Headlines",
    title: "Extra headline pack",
    blurb: "One more story card per brief, a different hook.",
    cost: 10,
    effect: "Adds a bonus headline card to every story.",
    grants: {
      headlineTemplateIds: [
        "hl-vet-label",
        "hl-bowl-label",
        "hl-phone-label",
      ],
    },
  },
  {
    id: "sound-sting-pack",
    category: "sfx",
    tool: "sound",
    sub: "Stings",
    title: "Sting pack",
    blurb: "Unlock alarm/sting beds and sting treatment in Sound.",
    cost: 10,
    effect: "Unlocks alarm and sting beds for tense moments.",
    grants: { features: ["sting-pack"] },
  },
  {
    id: "sfx-pack",
    category: "sfx",
    tool: "sound",
    sub: "SFX",
    title: "Extra sound hits",
    blurb: "Alert, glitch, and success hits for punchy moments.",
    cost: 10,
    effect: "Unlocks the Alert, Glitch, and Success sound hits.",
    grants: {
      sfxOptionIds: [
        "sfx-opt-notify-alert",
        "sfx-opt-glitch",
        "sfx-opt-success",
      ],
    },
  },
  {
    id: "looks-pack",
    category: "looks",
    tool: "look",
    sub: "Filters",
    title: "Extra looks",
    blurb: "Punchy contrast and soft fade grades.",
    cost: 10,
    effect: "Unlocks the Punchy and Soft Fade looks.",
    grants: {
      lookIds: ["look-high-contrast", "look-soft-fade"],
    },
  },
  {
    id: "motion-pack",
    category: "motion",
    tool: "motion",
    sub: "Camera moves",
    title: "Extra camera moves",
    blurb: "Handheld shake and snap zoom moves.",
    cost: 10,
    effect: "Unlocks the Handheld and Snap Zoom camera moves.",
    grants: {
      motionIds: ["motion-handheld", "motion-snap-zoom"],
    },
  },
  {
    id: "sticker-pack-reactions",
    category: "decorate",
    tool: "decorate",
    sub: "Reactions",
    title: "Reactions pack",
    blurb: "Big reaction stickers for the loudest beats.",
    cost: 10,
    effect:
      "Unlocks the Skull, Sob, Shock, Hundred, Siren, Speech, Clown, and Alien stickers.",
    grants: {
      stickerIds: [
        "sticker-skull",
        "sticker-sob",
        "sticker-shock",
        "sticker-hundred",
        "sticker-siren",
        "sticker-speech",
        "sticker-clown",
        "sticker-alien",
      ],
    },
  },
  {
    id: "sticker-pack-effects",
    category: "decorate",
    tool: "decorate",
    sub: "Effects",
    title: "Effects pack",
    blurb: "Shapes, marks, and props to punch up a frame.",
    cost: 12,
    effect:
      "Unlocks the Burst, Star, Frame, Dot, Underline, Ring, Censor, Wait, Top Hat, and Party Hat stickers.",
    grants: {
      stickerIds: [
        "sticker-burst",
        "sticker-star",
        "sticker-frame",
        "sticker-dot",
        "sticker-underline",
        "sticker-ring",
        "sticker-censor",
        "sticker-wait",
        "sticker-tophat",
        "sticker-partyhat",
      ],
    },
  },
  {
    id: "seamless-transitions",
    category: "utility",
    tool: "cut",
    sub: "Flow",
    title: "Seamless Transitions",
    blurb: "Smooths hard jumps between clips so cuts stop screaming edited.",
    cost: 12,
    effect: "Reduces the Doubt that jump cuts add to your reel.",
    grants: { features: ["seamless-transitions"] },
  },
  {
    id: "precision-trim",
    category: "utility",
    tool: "cut",
    sub: "Trim",
    title: "Precision trim",
    blurb: "Exact cut points so you can trim a coarse clip to where the clean content ends.",
    cost: 12,
    effect: "Lets you trim a leaky beat to its clean end, clearing its poor-editing Doubt.",
    grants: { features: ["precision-trim"] },
  },
  {
    id: "extra-draft-slot",
    category: "utility",
    tool: "cut",
    sub: "Workflow",
    title: "Extra draft slot",
    blurb: "One more in-progress draft before eviction.",
    cost: 10,
    effect: "Adds one more in-progress draft slot.",
    grants: { features: ["draft-slot"] },
  },
  {
    id: "export-card-theme",
    category: "cosmetic",
    tool: "look",
    sub: "Card themes",
    title: "Export card theme",
    blurb: "Alternate result-card chrome for your portfolio stills.",
    cost: 8,
    effect: "Unlocks an alternate result-card theme (cosmetic).",
    grants: { themes: ["portfolio-alt"] },
  },
] as const;

const BY_ID = new Map(STORE_CATALOG.map((item) => [item.id, item]));

/** option kind+id → granting store item (premium only). */
const OPTION_TO_ITEM = buildOptionToItemMap();

function buildOptionToItemMap(): Map<string, StoreItemId> {
  const map = new Map<string, StoreItemId>();
  const put = (kind: CreativeOptionKind, id: string, itemId: StoreItemId) => {
    const key = optionKey(kind, id);
    if (map.has(key)) {
      throw new Error(`Duplicate store grant for ${key}`);
    }
    map.set(key, itemId);
  };
  for (const item of STORE_CATALOG) {
    for (const id of item.grants.headlineTemplateIds ?? []) {
      put("headline", id, item.id);
    }
    for (const id of item.grants.sfxOptionIds ?? []) {
      put("sfx", id, item.id);
    }
    for (const id of item.grants.lookIds ?? []) {
      put("look", id, item.id);
    }
    for (const id of item.grants.motionIds ?? []) {
      put("motion", id, item.id);
    }
    for (const id of item.grants.stickerIds ?? []) {
      put("sticker", id, item.id);
    }
    for (const id of item.grants.transitionIds ?? []) {
      put("transition", id, item.id);
    }
  }
  return map;
}

export function optionKey(kind: CreativeOptionKind, optionId: string): string {
  return `${kind}:${optionId}`;
}

export function getStoreItem(id: StoreItemId): StoreCatalogItem {
  const item = BY_ID.get(id);
  if (!item) throw new Error(`Unknown store item: ${id}`);
  return item;
}

export function tryGetStoreItem(id: string): StoreCatalogItem | null {
  return BY_ID.get(id as StoreItemId) ?? null;
}

export function knownStoreItemIds(): ReadonlySet<string> {
  return new Set(STORE_CATALOG.map((i) => i.id));
}

export function itemsInCategory(category: StoreCategoryId): readonly StoreCatalogItem[] {
  return STORE_CATALOG.filter((i) => i.category === category);
}

export function itemsForTool(tool: ToolId): readonly StoreCatalogItem[] {
  return STORE_CATALOG.filter((i) => i.tool === tool);
}

export interface StoreSubGroup {
  sub: string;
  items: readonly StoreCatalogItem[];
}

/** Items for a tool, grouped by sub-category in catalog order. */
export function storeSubGroupsForTool(tool: ToolId): readonly StoreSubGroup[] {
  const groups: StoreSubGroup[] = [];
  for (const item of itemsForTool(tool)) {
    let group = groups.find((g) => g.sub === item.sub);
    if (!group) {
      group = { sub: item.sub, items: [] };
      groups.push(group);
    }
    (group.items as StoreCatalogItem[]).push(item);
  }
  return groups;
}

export function isStoreItemOwned(
  purchases: readonly StoreItemId[],
  itemId: StoreItemId,
): boolean {
  return purchases.includes(itemId);
}

export function ownedStoreCount(purchases: readonly StoreItemId[]): number {
  return purchases.filter((id) => BY_ID.has(id)).length;
}

export function hasStoreFeature(
  purchases: readonly StoreItemId[],
  feature: StoreFeatureId,
): boolean {
  return STORE_CATALOG.some(
    (item) =>
      purchases.includes(item.id) && (item.grants.features ?? []).includes(feature),
  );
}

export function isCreativeOptionFree(
  kind: CreativeOptionKind,
  optionId: string,
): boolean {
  return FREE_CREATIVE_OPTIONS[kind].has(optionId);
}

/** Item that unlocks this option, if any (null when free / unknown). */
export function storeItemForCreativeOption(
  kind: CreativeOptionKind,
  optionId: string,
): StoreCatalogItem | null {
  if (isCreativeOptionFree(kind, optionId)) return null;
  const itemId = OPTION_TO_ITEM.get(optionKey(kind, optionId));
  return itemId ? getStoreItem(itemId) : null;
}

/**
 * Whether the player may newly apply this creative option.
 * Clear / null ops are always free (handled by callers).
 * `bypass` covers challenge / QA so ownership never blocks interpreting play.
 */
export function isCreativeOptionOwned(
  kind: CreativeOptionKind,
  optionId: string,
  purchases: readonly StoreItemId[],
  opts?: { bypass?: boolean },
): boolean {
  if (opts?.bypass) return true;
  if (isCreativeOptionFree(kind, optionId)) return true;
  const item = storeItemForCreativeOption(kind, optionId);
  if (!item) {
    // Unknown / unmapped IDs stay usable so old/challenge graphs never soft-lock.
    return true;
  }
  return purchases.includes(item.id);
}

/** All option IDs granted by owned items for a kind (plus free set). */
export function ownedCreativeOptionIds(
  kind: CreativeOptionKind,
  purchases: readonly StoreItemId[],
): ReadonlySet<string> {
  const out = new Set(FREE_CREATIVE_OPTIONS[kind]);
  for (const itemId of purchases) {
    const item = BY_ID.get(itemId);
    if (!item) continue;
    const list =
      kind === "headline"
        ? item.grants.headlineTemplateIds
        : kind === "sfx"
          ? item.grants.sfxOptionIds
          : kind === "look"
            ? item.grants.lookIds
            : kind === "motion"
              ? item.grants.motionIds
              : kind === "sticker"
                ? item.grants.stickerIds
                : item.grants.transitionIds;
    for (const id of list ?? []) out.add(id);
  }
  return out;
}

export function categoryInventory(
  category: StoreCategoryId,
  purchases: readonly StoreItemId[],
): { owned: StoreCatalogItem[]; locked: StoreCatalogItem[] } {
  const items = itemsInCategory(category);
  const owned: StoreCatalogItem[] = [];
  const locked: StoreCatalogItem[] = [];
  for (const item of items) {
    if (purchases.includes(item.id)) owned.push(item);
    else locked.push(item);
  }
  return { owned, locked };
}

/** Human labels for grants — pass current pack catalogs / label lookup. */
export function grantContentLabels(
  item: StoreCatalogItem,
  labelFor: (kind: CreativeOptionKind, id: string) => string | null,
): string[] {
  const lines: string[] = [];
  const push = (kind: CreativeOptionKind, ids: readonly string[] | undefined) => {
    if (!ids?.length) return;
    const labels = ids.map((id) => labelFor(kind, id) ?? id);
    if (kind === "headline" && labels.length > 1) {
      lines.push(`Bonus headlines (${labels.length} stories)`);
      return;
    }
    lines.push(...labels);
  };
  push("headline", item.grants.headlineTemplateIds);
  push("sfx", item.grants.sfxOptionIds);
  push("look", item.grants.lookIds);
  push("motion", item.grants.motionIds);
  push("sticker", item.grants.stickerIds);
  push("transition", item.grants.transitionIds);
  for (const feature of item.grants.features ?? []) {
    lines.push(featureLabel(feature));
  }
  for (const theme of item.grants.themes ?? []) {
    if (theme === "portfolio-alt") lines.push("Portfolio card chrome");
  }
  return lines;
}

function featureLabel(feature: StoreFeatureId): string {
  switch (feature) {
    case "precision-trim":
      return "Trim leaky beats clean";
    case "caption-deep":
      return "Extra caption styles";
    case "sting-pack":
      return "Alarm / sting bed";
    case "draft-slot":
      return "+1 draft slot";
    case "seamless-transitions":
      return "Softer jump cuts";
    default:
      return feature;
  }
}

export function resolveResultCardTheme(args: {
  purchases: readonly StoreItemId[];
  equippedTheme?: ResultCardThemeId | null;
}): ResultCardThemeId {
  const ownedAlt = args.purchases.includes("export-card-theme");
  const equipped = args.equippedTheme ?? null;
  if (equipped === "portfolio-alt" && ownedAlt) return "portfolio-alt";
  if (equipped === "default") return "default";
  // Legacy: owning the theme auto-used portfolio-alt before equip existed.
  if (ownedAlt && (equipped == null || equipped === undefined)) {
    return "portfolio-alt";
  }
  return "default";
}

export function sanitizeEquippedTheme(
  purchases: readonly StoreItemId[],
  equippedTheme: unknown,
): ResultCardThemeId {
  if (equippedTheme === "portfolio-alt") {
    return purchases.includes("export-card-theme") ? "portfolio-alt" : "default";
  }
  if (equippedTheme === "default") return "default";
  // Missing field: preserve old auto-equip when owned.
  return purchases.includes("export-card-theme") ? "portfolio-alt" : "default";
}

/** Kid-facing badge when a selected premium option is not in the player's bag. */
export function selectedOptionBadge(args: {
  owned: boolean;
  inChallenge: boolean;
}): string | null {
  if (args.owned) return null;
  return args.inChallenge ? "Challenge edit" : "Not in bag";
}
